import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";

export type NewOrgStep = "basics" | "dataAccess" | "branding";

const STEP_ORDER: NewOrgStep[] = ["basics", "dataAccess", "branding"];

export function newOrgStepIndex(step: NewOrgStep): number {
  return STEP_ORDER.indexOf(step);
}

interface NewOrgStepStripProps {
  activeStep: NewOrgStep;
  maxVisitedStep: NewOrgStep;
  onStepChange: (step: NewOrgStep) => void;
}

/** Numbered step strip for the new-organization create drawer (Wonder step tabs). */
export function NewOrgStepStrip({
  activeStep,
  maxVisitedStep,
  onStepChange,
}: NewOrgStepStripProps) {
  const { t } = useTranslation();
  const maxIndex = newOrgStepIndex(maxVisitedStep);
  const activeIndex = newOrgStepIndex(activeStep);

  return (
    <div className="flex shrink-0 border-b border-border px-4">
      <div className="flex min-w-0 flex-1 items-stretch">
        {STEP_ORDER.map((step, index) => {
          const isActive = step === activeStep;
          const isDone = index < activeIndex;
          const isReachable = index <= maxIndex;
          const number = index + 1;
          const label =
            step === "basics"
              ? t("iam.newOrgDialog.steps.basics", "Basics")
              : step === "dataAccess"
                ? t("iam.newOrgDialog.steps.dataAccess", "Data access")
                : t("iam.newOrgDialog.steps.branding", "Branding");

          return (
            <button
              key={step}
              type="button"
              disabled={!isReachable}
              onClick={() => isReachable && onStepChange(step)}
              data-testid={`new-org-step-${step}`}
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 border-b-2 px-2.5 py-2.5 text-xs font-medium transition-colors",
                isActive
                  ? "border-primary text-foreground"
                  : isDone
                    ? "border-transparent text-foreground"
                    : "border-transparent text-muted-foreground",
                isReachable && !isActive && "hover:text-foreground",
                !isReachable && "cursor-not-allowed opacity-50",
              )}
            >
              <span
                className={cn(
                  "inline-flex size-4 items-center justify-center rounded-full text-[10px] font-semibold leading-none",
                  isDone
                    ? "bg-success-subtle-foreground text-white"
                    : isActive
                      ? "bg-primary text-primary-foreground"
                      : "border border-input-border text-muted-foreground",
                )}
              >
                {isDone ? <Check className="size-2.5" strokeWidth={3} /> : number}
              </span>
              {label}
              {step === "branding" && (
                <span className="rounded-sm bg-border-subtle px-1.5 py-[3px] text-[10px] font-normal leading-none text-muted-foreground">
                  {t("iam.newOrgDialog.steps.optionalTag", "Optional")}
                </span>
              )}
            </button>
          );
        })}
        <div className="min-w-0 flex-1" aria-hidden="true" />
        <div className="flex items-center px-2">
          <span className="text-[11px] text-muted-foreground">
            {activeStep === "basics"
              ? t("iam.newOrgDialog.steps.onlyNameRequired", "Only the name is required")
              : t("iam.newOrgDialog.steps.everythingOptional", "Everything here is optional")}
          </span>
        </div>
      </div>
    </div>
  );
}
